// «Primeros pasos» (A-04): checklist de activación en «Qué hacer hoy». Sustituye a la pantalla de bienvenida
// de un solo botón. Cada paso se marca solo según los datos reales de la cuenta; desaparece al completar los 5 (v26g: «Revisa tu perfil en redes»).
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { Check, X } from "lucide-react";

export default function FirstSteps() {
  const { user } = useAuth();
  const key = user?.id ? `ca_firststeps_hidden_${user.id}` : "";
  const [hidden, setHidden] = useState(() => {
    try { return !!(key && localStorage.getItem(key)); } catch { return false; }
  });
  const [state, setState] = useState(null);

  const load = useCallback(async () => {
    const [brain, ig, job, prospects] = await Promise.all([
      api.get("/business-brain").then((r) => r.data || {}).catch(() => ({})),
      api.get("/instagram/link").then((r) => r.data || {}).catch(() => ({})),
      api.get("/opportunities/search/current").then((r) => r.data?.job || null).catch(() => null),
      api.get("/prospects").then((r) => r.data || []).catch(() => []),
    ]);
    setState({
      onboarding: !!brain.onboarding_completed,
      approved: !!brain.summary_approved,
      profile: !!ig.linked,
      searched: !!job,
      saved: Array.isArray(prospects) && prospects.some((p) => p.is_demo !== true),
    });
  }, []);

  useEffect(() => {
    if (hidden) return undefined;
    load();
    window.addEventListener("ca:data-changed", load);
    return () => window.removeEventListener("ca:data-changed", load);
  }, [hidden, load]);

  if (hidden || !state) return null;
  const steps = [
    { done: state.onboarding, t: "Cuéntanos tu negocio", s: "Oferta, objetivos y perfiles.", to: "/app/brain", cta: "Completar" },
    { done: state.approved, t: "Aprueba el resumen de tu negocio", s: "Es la base de todas las búsquedas y mensajes.", to: "/app/brain", cta: "Ir a Mi negocio" },
    { done: state.profile, t: "Revisa tu perfil en redes", s: "Antes de escribir a nadie, mira si tu Instagram transmite confianza.", to: "/app/canales", cta: "Revisar mi perfil" },
    { done: state.searched, t: "Lanza tu primera búsqueda", s: "Te preparamos hasta 5 oportunidades con encaje.", to: "/app/oportunidades", cta: "Buscar oportunidades" },
    { done: state.saved, t: "Guarda tu primer contacto", s: "Así empieza a llenarse tu agenda de cada mañana.", to: "/app/oportunidades", cta: "Ver oportunidades" },
  ];
  const doneN = steps.filter((x) => x.done).length;
  if (doneN === steps.length) return null;
  const next = steps.findIndex((x) => !x.done);
  const pct = Math.round((doneN / steps.length) * 100);

  const hide = () => {
    try { if (key) localStorage.setItem(key, "1"); } catch { /* sin almacenamiento: se oculta solo en esta visita */ }
    setHidden(true);
  };

  return (
    <section data-testid="first-steps" className="ca-card ca-card-gold" style={{ padding: 22 }}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-4">
          <div aria-hidden="true" style={{ width: 52, height: 52, borderRadius: "50%", flex: "none", display: "grid", placeItems: "center",
            background: `conic-gradient(var(--ca-gold) ${pct}%, rgba(255,255,255,.07) 0)` }}>
            <span style={{ width: 42, height: 42, borderRadius: "50%", display: "grid", placeItems: "center", background: "var(--ca-surface)", fontSize: 13, fontWeight: 600, color: "var(--ca-text)" }} className="tnum">{doneN}/{steps.length}</span>
          </div>
          <div>
            <span className="ca-eyebrow">Primeros pasos</span>
            <p className="mt-1 text-[17px] font-semibold text-[var(--ca-text)]">Tu primera agenda, en {steps.length - doneN} {steps.length - doneN === 1 ? "paso" : "pasos"}</p>
          </div>
        </div>
        <button type="button" onClick={hide} data-testid="first-steps-hide" className="text-[var(--ca-subtle)] hover:text-[var(--ca-text)]" title="Ocultar" aria-label="Ocultar primeros pasos">
          <X size={18} />
        </button>
      </div>

      <ol className="mt-5 grid gap-2.5" style={{ listStyle: "none", padding: 0, margin: "20px 0 0" }}>
        {steps.map((st, i) => {
          const isNext = i === next;
          return (
            <li key={st.t} data-testid={`first-step-${i + 1}`} data-done={st.done ? "1" : "0"}
              className="flex flex-wrap items-center gap-3.5"
              style={{ padding: "12px 14px", borderRadius: 14, border: `1px solid ${isNext ? "rgba(224,185,112,.35)" : "var(--ca-line)"}`, background: isNext ? "rgba(224,185,112,.06)" : "transparent" }}>
              <span aria-hidden="true" style={{ width: 28, height: 28, borderRadius: "50%", flex: "none", display: "grid", placeItems: "center", fontSize: 12, fontWeight: 600,
                background: st.done ? "var(--ca-metal)" : "transparent", color: st.done ? "var(--ca-gold-ink)" : "var(--ca-gold)",
                border: st.done ? 0 : "1px solid rgba(224,185,112,.45)" }}>
                {st.done ? <Check size={15} strokeWidth={2.6} /> : i + 1}
              </span>
              <div className="min-w-0 flex-1">
                <b className="block text-[14.5px]" style={{ color: st.done ? "var(--ca-muted)" : "var(--ca-text)", textDecoration: st.done ? "line-through" : "none", textDecorationColor: "rgba(255,255,255,.25)" }}>{st.t}</b>
                {!st.done && <small className="text-[13px] text-[var(--ca-muted)]">{st.s}</small>}
              </div>
              {isNext && (
                <Link to={st.to} data-testid={`first-step-cta-${i + 1}`} className="ca-btn-gold ca-btn-sm">{st.cta} →</Link>
              )}
            </li>
          );
        })}
      </ol>
    </section>
  );
}
